/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid engine/virtualizer.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import {
  elementScroll,
  measureElement,
  observeElementOffset,
  observeElementRect,
  Virtualizer
} from '@tanstack/virtual-core'

export type GridVirtualizerInstance = Virtualizer<HTMLDivElement, Element>

export interface GridVirtualizer {
  instance: GridVirtualizerInstance
  setCount: (count: number) => void
  // Discards cached row measurements. Rows are measured by INDEX, so any
  // transition that puts different data at the same index (sorting,
  // filtering, paging, a data swap) leaves the cache describing rows that are
  // no longer there.
  remeasure: () => void
  destroy: () => void
}

export interface GridVirtualizerOptions {
  // Measure each rendered row instead of trusting rowHeight. rowHeight stays
  // the estimate for rows that have not been rendered yet, so it still sizes
  // the scrollbar.
  autoRowHeight: boolean
  count: number
  getScrollElement: () => HTMLDivElement
  onChange: () => void
  overscan: number
  rowHeight: number
}

export const createGridVirtualizer = (options: GridVirtualizerOptions): GridVirtualizer => {
  const instance: GridVirtualizerInstance = new Virtualizer({
    count: options.count,
    estimateSize: () => options.rowHeight,
    getScrollElement: options.getScrollElement,
    observeElementOffset,
    observeElementRect,
    onChange: options.onChange,
    overscan: options.overscan,
    scrollToFn: elementScroll,
    ...(options.autoRowHeight && { measureElement })
  })

  const unmount = instance._didMount()
  instance._willUpdate()

  return {
    instance,
    setCount(count: number) {
      if (count !== instance.options.count) {
        instance.setOptions({ ...instance.options, count })
      }
    },
    remeasure() {
      if (options.autoRowHeight) {
        instance.measure()
      }
    },
    destroy() {
      unmount()
    }
  }
}
